// src/components/ControlMantenimiento.js
//
// Ruta mínima de administración: /control-mantenimiento
// Solo permite reactivar el Club cuando está en mantenimiento.
// Exige auth + rol admin validado server-side.
// No muestra ninguna información ni pantalla del Club.

import React, { useState, useEffect } from 'react'
import { auth, db } from '../firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'

const cambiarEstadoClubFn = httpsCallable(getFunctions(undefined, 'us-central1'), 'cambiarEstadoClub')

const ESTADO = { CARGANDO: 'cargando', SIN_AUTH: 'sin_auth', NO_ADMIN: 'no_admin', LISTO: 'listo' }

export default function ControlMantenimiento() {
  const [fase, setFase]             = useState(ESTADO.CARGANDO)
  const [estadoClub, setEstadoClub] = useState(null)
  const [email, setEmail]           = useState('')
  const [password, setPassword]     = useState('')
  const [error, setError]           = useState('')
  const [trabajando, setTrabajando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const [mensaje, setMensaje]       = useState('')

  // Verificar auth + rol admin
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setFase(ESTADO.SIN_AUTH); return }
      try {
        const snap = await import('firebase/firestore').then(({ getDoc }) =>
          getDoc(doc(db, 'usuarios', user.uid))
        )
        if (!snap.exists() || snap.data().rol !== 'admin') {
          setFase(ESTADO.NO_ADMIN)
          await signOut(auth)
        } else {
          setFase(ESTADO.LISTO)
        }
      } catch {
        setFase(ESTADO.NO_ADMIN)
      }
    })
    return () => unsub()
  }, [])

  // Suscribirse al estado del Club
  useEffect(() => {
    if (fase !== ESTADO.LISTO) return
    const unsub = onSnapshot(doc(db, 'configuracion_publica', 'club'), (snap) => {
      if (snap.exists()) {
        setEstadoClub(snap.data())
      } else {
        setEstadoClub({ estado: 'activo', mensaje: '' })
      }
    })
    return () => unsub()
  }, [fase])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setTrabajando(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch {
      setError('Email o contraseña incorrectos.')
    } finally {
      setTrabajando(false)
    }
  }

  const handleCambiarEstado = async (nuevoEstado) => {
    setConfirmando(false)
    setTrabajando(true)
    setError('')
    try {
      await cambiarEstadoClubFn({ estado: nuevoEstado, mensaje })
      setMensaje('')
    } catch (err) {
      setError(err.message || 'Error al cambiar estado.')
    } finally {
      setTrabajando(false)
    }
  }

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (fase === ESTADO.CARGANDO) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-4 border-maja-red border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // ─── Login ────────────────────────────────────────────────────────────────
  if (fase === ESTADO.SIN_AUTH || fase === ESTADO.NO_ADMIN) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-sm">
          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-6 text-center">
            Control de mantenimiento
          </p>
          {fase === ESTADO.NO_ADMIN && (
            <p className="text-sm text-red-600 text-center mb-4">Acceso restringido a administradores.</p>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email administrador"
              required
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-maja-red"
            />
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Contraseña"
              required
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-maja-red"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={trabajando}
              className="w-full bg-maja-red text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
            >
              {trabajando ? 'Verificando...' : 'Ingresar'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ─── Panel de control ────────────────────────────────────────────────────
  const enMantenimiento = estadoClub?.estado === 'mantenimiento'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-md">
        <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-2 text-center">
          Control de mantenimiento
        </p>

        {/* Estado actual */}
        <div className={`flex items-center justify-center gap-2 mt-4 mb-6 px-4 py-3 rounded-xl ${
          enMantenimiento ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'
        }`}>
          <span className={`w-2.5 h-2.5 rounded-full ${enMantenimiento ? 'bg-amber-400' : 'bg-green-500'}`} />
          <span className={`text-sm font-semibold ${enMantenimiento ? 'text-amber-700' : 'text-green-700'}`}>
            {enMantenimiento ? 'Club en mantenimiento' : 'Club activo'}
          </span>
        </div>

        {estadoClub?.actualizadoPor && (
          <p className="text-xs text-gray-400 text-center mb-6">
            Última actualización: {estadoClub.actualizadoEn?.toDate?.()?.toLocaleString?.('es-AR') || '—'}
          </p>
        )}

        {/* Confirmación de acción */}
        {confirmando ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 text-center">
              {enMantenimiento
                ? '¿Reactivar el Club? Todos los usuarios podrán acceder nuevamente.'
                : '¿Pausar el Club? Nadie podrá acceder hasta que lo reactives.'}
            </p>
            {!enMantenimiento && (
              <textarea
                value={mensaje}
                onChange={e => setMensaje(e.target.value)}
                placeholder="Mensaje para los usuarios (opcional)"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-maja-red resize-none"
              />
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmando(false)}
                className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-3 text-sm font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleCambiarEstado(enMantenimiento ? 'activo' : 'mantenimiento')}
                disabled={trabajando}
                className={`flex-1 text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 ${
                  enMantenimiento ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {trabajando ? 'Procesando...' : (enMantenimiento ? 'Sí, reactivar' : 'Sí, pausar')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={() => setConfirmando(true)}
              className={`w-full text-white rounded-xl py-3 text-sm font-semibold ${
                enMantenimiento ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-500 hover:bg-amber-600'
              }`}
            >
              {enMantenimiento ? 'Reactivar Club' : 'Pausar Club'}
            </button>
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
            <button
              onClick={() => signOut(auth)}
              className="w-full text-xs text-gray-400 hover:text-gray-600 py-2"
            >
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
